import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash, UploadSimple, Eye, Phone, ChatText, AddressBook, EnvelopeSimple } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shell } from "@/components/Shell";
import { AvatarDisplay } from "@/components/Avatar";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

export default function Profile() {
  const { t, setUser } = useApp();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPrev, setShowPrev] = useState(false);
  const [prefs, setPrefs] = useState({ notify_scan: true, notify_message: true });
  const fileRef = useRef();

  useEffect(() => { api.get("/profile").then((r) => setP(r.data)); }, []);
  useEffect(() => { api.get("/settings/email").then((r) => setPrefs(r.data)).catch(() => {}); }, []);

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      const res = await api.put("/settings/email", next);
      setPrefs(res.data);
      toast.success(t("saved"));
    } catch {
      toast.error("Fehler");
      api.get("/settings/email").then((r) => setPrefs(r.data)).catch(() => {});
    }
  };

  if (!p) return <Shell back="/dashboard"><div className="pt-20 text-center font-heading text-gray-500">…</div></Shell>;

  const setField = (k, v) => setP({ ...p, [k]: v });

  const addContact = () => setP({ ...p, contacts: [...(p.contacts || []), { id: crypto.randomUUID(), name: "", relationship: "", phone: "" }] });
  const updContact = (i, k, v) => { const c = [...p.contacts]; c[i] = { ...c[i], [k]: v }; setP({ ...p, contacts: c }); };
  const rmContact = (i) => setP({ ...p, contacts: p.contacts.filter((_, idx) => idx !== i) });
  const contactsSupported = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;
  const fromPhonebook = async () => {
    if (!contactsSupported) {
      toast.message("Die Kontaktauswahl wird auf diesem Gerät/Browser nicht unterstützt – bitte manuell eingeben.");
      addContact();
      return;
    }
    try {
      const r = await navigator.contacts.select(["name", "tel"], { multiple: false });
      if (r && r[0]) {
        const n = Array.isArray(r[0].name) ? r[0].name[0] : r[0].name;
        const ph = Array.isArray(r[0].tel) ? r[0].tel[0] : r[0].tel;
        setP((prev) => ({ ...prev, contacts: [...(prev.contacts || []), { id: crypto.randomUUID(), name: n || "", relationship: "", phone: ph || "" }] }));
      }
    } catch { /* user cancelled or permission denied */ }
  };

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP({ ...p, avatar_type: "upload", avatar_value: res.data.path });
      toast.success("Bild hochgeladen");
    } catch { toast.error("Upload fehlgeschlagen"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/profile", {
        first_name: p.first_name, last_name: p.last_name, avatar_type: p.avatar_type, avatar_value: p.avatar_value,
        contacts: p.contacts, notes: p.notes, is_active: p.is_active,
        allow_calls: p.allow_calls !== false, allow_messages: p.allow_messages !== false,
      });
      setP(res.data);
      setUser((u) => (u ? { ...u, first_name: res.data.first_name, last_name: res.data.last_name } : u));
      toast.success(t("saved"));
    } catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  };

  return (
    <Shell back="/dashboard">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-heading font-semibold text-gray-900">{t("myProfile")}</h1>
          <button data-testid="profile-preview-btn" onClick={() => setShowPrev(true)} className="sb-btn px-3 py-2 bg-white flex items-center gap-1.5 text-sm font-medium"><Eye size={18} weight="bold" />{t("preview")}</button>
        </div>

        <div className="sb-card p-5">
          <div className="flex items-center gap-4">
            <AvatarDisplay type={p.avatar_type} value={p.avatar_value} size={80} />
            <div className="flex-1 space-y-2">
              <input data-testid="profile-firstname" className="sb-input" placeholder={t("firstName")} value={p.first_name || ""} onChange={(e) => setField("first_name", e.target.value)} />
              <input data-testid="profile-lastname" className="sb-input" placeholder={t("lastName")} value={p.last_name || ""} onChange={(e) => setField("last_name", e.target.value)} />
              <p className="text-xs font-body text-gray-400">{t("profileNameHint")}</p>
            </div>
          </div>

          <p className="text-xs font-body text-gray-500 uppercase tracking-wider mt-5 mb-2">{t("chooseAvatar")}</p>
          <div className="mt-2">
            <button data-testid="motif-upload" onClick={() => fileRef.current?.click()}
              className="sb-btn px-4 py-2 bg-white font-medium text-sm flex items-center gap-2">
              <UploadSimple size={18} weight="bold" />{t("uploadImage")}
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onUpload} data-testid="avatar-file-input" />
          </div>
        </div>

        <div className="sb-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-heading font-semibold">{t("emergencyContacts")}</h2>
            <div className="flex items-center gap-2">
              <button data-testid="profile-phonebook" onClick={fromPhonebook} className="sb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1"><AddressBook size={16} weight="bold" />{t("fromPhonebook")}</button>
              <button data-testid="add-contact" onClick={addContact} className="p-2 rounded-xl border-2 border-gray-800 text-white active:scale-90 transition-transform" style={{ background: "var(--app-primary)" }}>
                <Plus size={18} weight="bold" />
              </button>
            </div>
          </div>
          <div className="space-y-4">
            {(p.contacts || []).map((c, i) => (
              <div key={c.id || i} className="space-y-2 pb-4 border-b border-gray-200 last:border-0" data-testid={`contact-row-${i}`}>
                <div className="flex gap-2">
                  <input className="sb-input" placeholder={t("name")} value={c.name} onChange={(e) => updContact(i, "name", e.target.value)} data-testid={`contact-name-${i}`} />
                  <button onClick={() => rmContact(i)} data-testid={`contact-remove-${i}`} className="p-3 rounded-xl border-2 border-gray-800 bg-white text-red-500 active:scale-90"><Trash size={18} weight="bold" /></button>
                </div>
                <input className="sb-input" placeholder={t("relationship")} value={c.relationship} onChange={(e) => updContact(i, "relationship", e.target.value)} data-testid={`contact-rel-${i}`} />
                <input className="sb-input" placeholder={t("phone")} value={c.phone} onChange={(e) => updContact(i, "phone", e.target.value)} data-testid={`contact-phone-${i}`} />
                <p className="text-xs font-body text-gray-400">{t("contactPhoneHint")}</p>
              </div>
            ))}
            {(!p.contacts || p.contacts.length === 0) && <p className="font-body text-gray-500 text-sm">—</p>}
          </div>
        </div>

        <div className="sb-card p-5 space-y-3">
          <div>
            <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("notes")}</label>
            <textarea data-testid="profile-notes" className="sb-input mt-1" rows={3} value={p.notes || ""} onChange={(e) => setField("notes", e.target.value)} />
          </div>
        </div>

        <div className="sb-card p-5">
          <h2 className="text-lg font-heading font-semibold mb-3">{t("contactOptions")}</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-body">{t("allowCalls")}</span>
              <Switch data-testid="profile-allow-calls" checked={p.allow_calls !== false} onCheckedChange={(v) => setField("allow_calls", v)} />
            </div>
            {p.allow_calls !== false && <p className="text-xs font-body text-gray-500 -mt-1">{t("callHint")}</p>}
            <div className="flex items-center justify-between">
              <span className="font-body">{t("allowMessages")}</span>
              <Switch data-testid="profile-allow-messages" checked={p.allow_messages !== false} onCheckedChange={(v) => setField("allow_messages", v)} />
            </div>
            {p.allow_messages !== false && <p className="text-xs font-body text-gray-500 -mt-1">{t("messageEmailHint")}</p>}
          </div>
        </div>

        <div className="sb-card p-5">
          <h2 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2"><EnvelopeSimple size={22} weight="duotone" />{t("emailNotifications")}</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-body">{t("notifyScan")}</span>
              <Switch data-testid="notify-scan-toggle" checked={!!prefs.notify_scan} onCheckedChange={(v) => savePrefs({ ...prefs, notify_scan: v })} />
            </div>
            <div className="flex items-center justify-between">
              <span className="font-body">{t("notifyMessage")}</span>
              <Switch data-testid="notify-message-toggle" checked={!!prefs.notify_message} onCheckedChange={(v) => savePrefs({ ...prefs, notify_message: v })} />
            </div>
          </div>
        </div>

        <button data-testid="save-profile" onClick={save} disabled={saving} className="sb-btn w-full py-4 text-white font-medium text-lg disabled:opacity-60" style={{ background: "var(--app-primary)" }}>
          {saving ? "…" : t("save")}
        </button>
      </motion.div>

      <Dialog open={showPrev} onOpenChange={setShowPrev}>
        <DialogContent data-testid="profile-preview-modal" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-center">{t("finderPreviewNote")}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center text-center gap-2">
            <AvatarDisplay type={p.avatar_type} value={p.avatar_value} size={90} />
            <p className="text-2xl font-heading font-semibold">{[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}</p>
          </div>
          <div className="space-y-2 mt-3">
            {(p.contacts || []).map((c, i) => (
              <div key={i} className="border-2 border-gray-800 rounded-2xl px-3 py-2 bg-white">
                <p className="font-heading text-sm">{c.name} <span className="text-gray-400 font-body">{c.relationship}</span></p>
                <p className="font-body text-sm text-gray-600">{c.phone}</p>
              </div>
            ))}
            {p.notes && <div className="border-2 border-gray-800 rounded-2xl px-3 py-2 bg-white"><p className="text-xs uppercase text-gray-500">{t("notes")}</p><p className="font-body text-sm text-gray-700">{p.notes}</p></div>}
          </div>
          <div className="flex gap-2 mt-4">
            {p.allow_calls !== false && <div className="sb-btn flex-1 py-2.5 text-white flex items-center justify-center gap-2 text-sm" style={{ background: "var(--app-primary)" }}><Phone size={16} weight="bold" />Anrufen</div>}
            {p.allow_messages !== false && <div className="sb-btn flex-1 py-2.5 bg-white flex items-center justify-center gap-2 text-sm"><ChatText size={16} weight="bold" />Nachricht</div>}
          </div>
          <button data-testid="profile-preview-close" onClick={() => setShowPrev(false)} className="sb-btn w-full py-3 mt-4 bg-white font-medium">{t("closeBtn")}</button>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
